import React, { useCallback, useMemo, useState } from 'react';
import { DataTable } from '../../components/organisms';
import { Button, Input } from '../../components/atoms';
import type { Column, TableAction } from '../../types/table';
import type { AppUpdate } from '../../types/api/app-update';
import { useAppUpdates } from '../../hooks/useAppUpdates';
import { getAppUpdateService } from '../../service/ServiceRegistry';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import toast from 'react-hot-toast';

export const AppUpdateManagementPage: React.FC = () => {
  const { fetchAppUpdates } = useAppUpdates();
  const appUpdateService = getAppUpdateService();

  const [refreshKey, setRefreshKey] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState<AppUpdate | null>(null);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<AppUpdate | null>(null);
  const [formAppLink, setFormAppLink] = useState('');
  const [formVersion, setFormVersion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingUpdate(null);
    setFormAppLink('');
    setFormVersion('');
    setIsFormModalOpen(true);
  }, []);

  const openEditModal = useCallback((row: AppUpdate) => {
    setEditingUpdate(row);
    setFormAppLink(row.appLink);
    setFormVersion(row.versionNumber);
    setIsFormModalOpen(true);
  }, []);

  const closeFormModal = useCallback(() => {
    setIsFormModalOpen(false);
    setEditingUpdate(null);
    setFormAppLink('');
    setFormVersion('');
  }, []);

  const handleSubmitForm = useCallback(async () => {
    if (!formAppLink.trim() || !formVersion.trim()) {
      toast.error('APK Download URL and Version Number are required');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingUpdate) {
        await appUpdateService.updateAppUpdate(editingUpdate.id, {
          appLink: formAppLink.trim(),
          versionNumber: formVersion.trim(),
        });
        toast.success('App update updated successfully');
      } else {
        await appUpdateService.createAppUpdate({
          appLink: formAppLink.trim(),
          versionNumber: formVersion.trim(),
        });
        toast.success('App update created successfully');
      }

      closeFormModal();
      handleRefresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save app update');
    } finally {
      setIsSubmitting(false);
    }
  }, [editingUpdate, formAppLink, formVersion, appUpdateService, closeFormModal, handleRefresh]);

  const handleDelete = useCallback((row: AppUpdate) => {
    setSelectedUpdate(row);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedUpdate) return;
    try {
      await appUpdateService.deleteAppUpdate(selectedUpdate.id);
      toast.success('App update deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedUpdate(null);
      handleRefresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete app update');
    }
  }, [selectedUpdate, appUpdateService, handleRefresh]);

  const columns = useMemo<Column<AppUpdate>[]>(
    () => [
      {
        id: 'versionNumber',
        header: 'Version',
        accessor: 'versionNumber',
      },
      {
        id: 'appLink',
        header: 'APK Download URL',
        accessor: 'appLink',
        cell: (row) => (
          <a
            href={row.appLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 break-all"
          >
            {row.appLink}
          </a>
        ),
        minWidth: '260px',
      },
      {
        id: 'createdAt',
        header: 'Created At',
        accessor: 'createdAt',
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {new Date(row.createdAt).toLocaleString()}
          </span>
        ),
      },
      {
        id: 'updatedAt',
        header: 'Updated At',
        accessor: 'updatedAt',
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
          </span>
        ),
      },
    ],
    []
  );

  const actions = useMemo<TableAction<AppUpdate>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        onClick: openEditModal,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        onClick: handleDelete,
      },
    ],
    [openEditModal, handleDelete]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">App Update Management</h1>
          <p className="text-gray-600 mt-1 text-left pl-1">
            Manage APK download URLs and version numbers for TV app updates.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={openCreateModal}>
            Add App Update
          </Button>
        </div>
      </div>

      <DataTable<AppUpdate>
        columns={columns}
        fetchData={fetchAppUpdates}
        initialPageSize={10}
        pageSizeOptions={[10, 20, 50]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="App update management table"
        key={refreshKey}
      />

      {/* Create / Edit Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingUpdate ? 'Edit App Update' : 'Create App Update'}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  APK Download URL
                </label>
                <Input
                  value={formAppLink}
                  onChange={(e) => setFormAppLink(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version Number
                </label>
                <Input
                  value={formVersion}
                  onChange={(e) => setFormVersion(e.target.value)}
                  placeholder="1.0.0"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="secondary" onClick={closeFormModal} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmitForm} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUpdate(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete App Update"
        message={
          selectedUpdate
            ? `Are you sure you want to delete app update version "${selectedUpdate.versionNumber}"? This action cannot be undone.`
            : 'Are you sure you want to delete this app update?'
        }
      />
    </div>
  );
};

export default AppUpdateManagementPage;

